import { ExtendedBasketAccountAddress } from '@epicuro-next/state/basket-state';
import { OrderType } from 'graphql-api/gql-api-interfaces';

import {
  BulkTypeDeliveryPeriod,
  BusinessState,
  DeliveryPeriodsByShop,
  DeliveryPeriodType,
  UiState,
} from '../../../models';
import { getDeliveryShopKey } from '../../../shared/shared';
import { updateShopsWithAnyDeliveryPeriod } from '../update-shops-with-new-selected-delivery-period/update-shops-with-any-delivery-period';
import { updateShopsWithBulkDeliveryPeriod } from '../update-shops-with-new-selected-delivery-period/update-shops-with-bulk-delivery-period';
import { updateShopsWithFutureDeliveryPeriod } from '../update-shops-with-new-selected-delivery-period/update-shops-with-future-delivery-period';
import { updateShopsWithNowDeliveryPeriod } from '../update-shops-with-new-selected-delivery-period/update-shops-with-now-delivery-period';
import { updateUiDeliveryState } from '../update-ui-delivery-state';

export function updateShops(
  deliveryPeriodsByShop: DeliveryPeriodsByShop,
  deliveryPeriodType: DeliveryPeriodType | null,
  selectedDate: string | BulkTypeDeliveryPeriod | null,
): DeliveryPeriodsByShop {
  switch (deliveryPeriodType) {
    case DeliveryPeriodType.Now:
      return updateShopsWithNowDeliveryPeriod(deliveryPeriodsByShop);

    case DeliveryPeriodType.Future:
      return updateShopsWithFutureDeliveryPeriod(
        deliveryPeriodsByShop,
        selectedDate as string | null,
      );

    case DeliveryPeriodType.Bulk:
      return updateShopsWithBulkDeliveryPeriod(
        deliveryPeriodsByShop,
        selectedDate as BulkTypeDeliveryPeriod | null,
      );

    default:
      return updateShopsWithAnyDeliveryPeriod(deliveryPeriodsByShop);
  }
}

/** Runs two times
 * 1. Once on successfully load product (loadProductSuccess)
 * 2. Once we add new basketItems from basket basketItemsSubscription in `order-product.component`
 */
export function preselectDeliveryPeriodsBusiness(
  state: BusinessState,
  {
    deliveryShops,
    preSelectedDeliveryPeriodType,
    preSelectedDeliveryPeriod,
    preSelectedOrderType,
  }: {
    deliveryShops: ExtendedBasketAccountAddress[];
    preSelectedDeliveryPeriodType: DeliveryPeriodType | null;
    preSelectedDeliveryPeriod: string | BulkTypeDeliveryPeriod | null;
    preSelectedOrderType: OrderType | null;
  },
): BusinessState {
  const shouldSelectAnyPeriod = preSelectedOrderType === OrderType.None;

  if (shouldSelectAnyPeriod) {
    if (!Object.keys(state.deliveryPeriods).length) {
      return {
        ...state,
      };
    }
    const deliveryPeriods = updateShops(state.deliveryPeriods, null, null);
    return {
      ...state,
      deliveryPeriods,
    };
  }

  if (!preSelectedDeliveryPeriodType) {
    return state;
  }

  // STEP 1: restore delivery periods from delivery shops, navigation from c-basket to config-strip
  const deliveryShopsDeliveryPeriods =
    deliveryShops
      ?.map((deliveryShop) => {
        const deliveryShopId = getDeliveryShopKey(deliveryShop);
        const deliveryPeriodsByShop =
          state.deliveryPeriods[deliveryShopId] ?? {};

        const createdDeliveryPeriodsByShop = {
          ...deliveryPeriodsByShop,
          currentDeliveryPeriodType: preSelectedDeliveryPeriodType,
        };

        // we don't construct delivery periods for Now type
        const deliveryPeriodsByType =
          preSelectedDeliveryPeriodType !== DeliveryPeriodType.Now
            ? {
                [preSelectedDeliveryPeriodType]: {
                  ...(deliveryPeriodsByShop?.[preSelectedDeliveryPeriodType] ??
                    {}),
                  selectedDeliveryPeriod: preSelectedDeliveryPeriod,
                },
              }
            : {};

        return {
          [deliveryShopId]: {
            ...createdDeliveryPeriodsByShop,
            ...deliveryPeriodsByType,
          },
        } as DeliveryPeriodsByShop;
      })
      .reduce((acc, curr) => ({ ...acc, ...curr }), {}) ?? {};

  return {
    ...state,
    // STEP 2: select date in each shop if possible, otherwise fallback to other date or Now period
    deliveryPeriods: updateShops(
      deliveryShopsDeliveryPeriods,
      preSelectedDeliveryPeriodType,
      preSelectedDeliveryPeriod,
    ),
  };
}

/** Runs two times
 * 1. Once on successfully load product (loadProductSuccess)
 * 2. Once we add new basketItems from basket basketItemsSubscription in `order-product.component`
 */
export function preselectDeliveryPeriodsUi(
  state: UiState,
  { deliveryPeriods }: { deliveryPeriods: DeliveryPeriodsByShop },
): UiState {
  const shouldSelectAnyPeriod = state.preSelectedOrderType === OrderType.None;

  if (shouldSelectAnyPeriod) {
    if (!Object.keys(deliveryPeriods).length) {
      return {
        ...state,
      };
    }

    const updatedDeliveryPeriods = updateShops(deliveryPeriods, null, null);
    const deliveryPeriodTypeFromFirstShop =
      updatedDeliveryPeriods[Object.keys(updatedDeliveryPeriods)[0]]
        .currentDeliveryPeriodType;
    const deliveryPeriodType =
      deliveryPeriodTypeFromFirstShop || DeliveryPeriodType.Now;

    return {
      ...updateUiDeliveryState(state, deliveryPeriodType),
    };
  }

  return state;
}
